import React from 'react';
import { Document, Page, StyleSheet, Text, View, Image } from '@react-pdf/renderer';
import { CORES_FCJ, CORES_FCJ_TEXTO } from '../utils/fcjCalculo';
import logoIcon from '../assets/concreto_tec_logo.png';
import { LogoRelatorio, AssinaturasRodape } from './pdf/PersonalizacaoRelatorio';

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 7,
    paddingTop: 22.68,
    paddingBottom: 92,
    paddingLeft: 28.35,
    paddingRight: 28.35,
  },
  headerBox: {
    border: '1pt solid #000',
    marginBottom: 4,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  headerTitleBlock: {
    flex: 1,
    padding: 6,
    alignItems: 'center',
    justifyContent: 'center',
    borderBottom: '1pt solid #000',
  },
  headerTitle: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
  },
  headerSubtitle: {
    fontSize: 9,
    textAlign: 'center',
    marginTop: 2,
  },
  headerNotice: {
    width: 220,
    padding: '4 8',
    justifyContent: 'center',
    borderLeft: '1pt solid #000',
  },
  headerNoticeText: {
    fontSize: 7,
    lineHeight: 1.35,
    color: '#444',
  },
  headerBrandBlock: {
    width: 120,
    padding: '4 6',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 3,
    borderLeft: '1pt solid #000',
    backgroundColor: '#fafafa',
  },
  headerBrandCaption: {
    fontSize: 6,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  placeholderBox: {
    border: '0.75pt dashed #999',
    alignItems: 'center',
    justifyContent: 'center',
  },
  placeholderText: {
    fontSize: 5,
    color: '#888',
    textAlign: 'center',
  },
  headerAddressText: {
    fontSize: 5,
    textAlign: 'center',
    lineHeight: 1.2,
    width: 100,
  },
  // Linha inferior do cabeçalho: coluna esquerda (faixa preta + 3 linhas de metadados)
  // + coluna do QR, irmãs no mesmo flex row — a coluna do QR estica (stretch padrão)
  // para cobrir a altura combinada da coluna esquerda inteira.
  headerLower: {
    flexDirection: 'row',
  },
  headerLowerLeft: {
    flex: 1,
    flexDirection: 'column',
  },
  headerIdBarRow: {
    flexDirection: 'row',
    borderBottom: '1pt solid #000',
  },
  headerIdBar: {
    flex: 1,
    backgroundColor: '#000',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4 8',
  },
  // Largura = headerNotice.width, para que headerIdBar (flex: 1) termine exatamente
  // onde headerTitleBlock termina, alinhado à linha acima.
  headerNoticeFill: {
    width: 220,
    borderLeft: '1pt solid #000',
  },
  headerIdBarLabel: {
    color: '#fff',
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    letterSpacing: 1,
  },
  headerIdBarValue: {
    color: '#fff',
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
  },

  clientImagePlaceholder: {
    width: 66,
    height: 40,
  },
  headerMetaRow: {
    flexDirection: 'row',
    borderBottom: '1pt solid #000',
  },
  headerMetaRowLast: {
    flexDirection: 'row',
  },
  metaCell: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: '4 6',
    borderRight: '1pt solid #000',
  },
  metaCellLast: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: '4 6',
  },
  // Largura percentual única para as 3 linhas de metadados (5 colunas de 20% cada), para
  // que as divisórias verticais fiquem alinhadas entre as linhas.
  metaCol: { width: '20%' },
  // Coluna do QR, à direita de headerLowerLeft, alinhada com a largura de
  // headerBrandBlock (logo) na linha de cima. Estica (stretch padrão) para cobrir a
  // altura combinada da faixa preta + as 3 linhas de metadados; ancorada no topo para
  // começar logo abaixo do "Endereço". Padding/gap/legenda reduzidos ao mínimo para que
  // o conteúdo do QR não seja mais alto que essas 4 linhas somadas — senão ele passa a
  // "puxar" headerLowerLeft para uma altura maior, sobrando espaço em branco abaixo de
  // headerMetaRowLast. Largura do QR (metaQrPlaceholder) não é alterada.
  headerQrColumn: {
    width: 120,
    borderLeft: '1pt solid #000',
    backgroundColor: '#fafafa',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '2 6',
    gap: 2,
  },
  metaQrPlaceholder: {
    width: 54,
    height: 54,
  },
  metaQrCaption: {
    fontSize: 4.5,
    lineHeight: 1.1,
    color: '#444',
    textAlign: 'center',
  },
  metaLabel: {
    fontFamily: 'Helvetica-Bold',
    marginRight: 4,
  },
  table: {
    marginTop: 19.84,
  },
  tableRow: {
    flexDirection: 'row',
    borderLeft: '1pt solid #000',
    borderRight: '1pt solid #000',
    borderBottom: '1pt solid #000',
  },
  tableHeaderRow: {
    borderTop: '1pt solid #000',
  },
  tableRowEven: {
    backgroundColor: '#f5f5f5',
  },
  th: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 6,
    padding: '4 2',
    borderRight: '1pt solid #000',
    textAlign: 'center',
  },
  thLast: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 6,
    padding: '4 2',
    textAlign: 'center',
  },
  td: {
    fontSize: 6,
    padding: '4 2',
    borderRight: '1pt solid #000',
    textAlign: 'center',
  },
  tdLast: {
    fontSize: 6,
    padding: '4 2',
    textAlign: 'center',
  },
  tdFcj: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 7,
  },
  colEtiqueta: { width: '7%' },
  colNotaFiscal: { width: '5%' },
  colVolume: { width: '4%' },
  colPeca: { width: '12%' },
  colSaidaUsina: { width: '5%' },
  colInicio: { width: '4%' },
  colFim: { width: '4%' },
  colMoldagem: { width: '5%' },
  colRuptura: { width: '5%' },
  colIdade: { width: '4%' },
  colSlump: { width: '5%' },
  colAgua: { width: '5%' },
  colTemp: { width: '5%' },
  colCarga: { width: '5%' },
  colFcj: { width: '4%' },
  colTipoRuptura: { width: '6%' },
  colPrensa: { flex: 1 },
  signatureRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 40,
  },
  signaturePlaceholder: {
    width: 220,
    height: 36,
  },
  footer: {
    position: 'absolute',
    bottom: 28.35,
    left: 28.35,
    right: 28.35,
    flexDirection: 'column',
    gap: 6,
  },
  footerPageRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  footerText: {
    fontSize: 7,
    color: '#666',
  },


  // Capa
  coverPage: {
    fontFamily: 'Helvetica',
    padding: 48,
  },
  coverBrandRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  coverIcon: {
    width: 48,
    height: 48,
  },
  coverBrandText: {
    marginLeft: 14,
  },
  coverWordmark: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
  },
  coverTagline: {
    fontSize: 8,
    letterSpacing: 1,
    color: '#0e7490',
    marginTop: 2,
  },
  coverSpacer: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  coverTitleBlock: {
    paddingTop: 40,
    borderTop: '1.5pt solid #000',
  },
  coverKicker: {
    fontSize: 9,
    letterSpacing: 1,
    color: '#666',
    marginBottom: 8,
  },
  coverTitle: {
    fontSize: 20,
    fontFamily: 'Helvetica-Bold',
    lineHeight: 1.25,
  },
  coverSubtitle: {
    fontSize: 10,
    color: '#666',
    marginTop: 6,
  },
  coverMetaGrid: {
    marginTop: 28,
  },
  coverMetaField: {
    marginBottom: 14,
  },
  coverMetaLabel: {
    fontSize: 7,
    letterSpacing: 0.5,
    color: '#888',
    marginBottom: 3,
  },
  coverMetaValue: {
    fontSize: 10,
    borderBottom: '0.75pt solid #ccc',
    paddingBottom: 5,
  },
  coverFooter: {
    paddingTop: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 8,
    color: '#888',
  },
});

// Exibe data (dd/mm/aaaa) no PDF.
function formatDateBrPdf(value) {
  if (!value) return '-';
  const s = String(value);
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
  }
  const p = new Date(s);
  if (Number.isNaN(p.getTime())) return s;
  return p.toLocaleDateString('pt-BR');
}

// Exibe um número com 2 casas decimais no PDF, ou '-' se ausente/inválido.
function formatNumeroPdf(value) {
  if (value == null || !Number.isFinite(Number(value))) return '-';
  return Number(value).toFixed(2);
}

// Exibe hora (HH:mm) no PDF, ou '-' se ausente.
function formatHoraPdf(value) {
  if (!value) return '-';
  const p = new Date(value);
  if (Number.isNaN(p.getTime())) return '-';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(p.getHours())}:${pad(p.getMinutes())}`;
}

// Documento PDF (@react-pdf) do relatório "Resultados sequencial" (Ensaio de Compressão Axial, NBR 5738/5739).
// Texto de aviso padrão do cabeçalho (classificação das prensas e restrição de reprodução do relatório).
const TEXTO_AVISO =
  'Todas as máquinas de ensaio (prensas de concreto) utilizadas no laboratório central são classificadas ' +
  'como classe 0 ou 1. Os resultados apresentados referem-se somente aos itens ensaiados. Não é permitida ' +
  'a reprodução total ou parcial deste relatório sem prévia autorização do cliente.';

function EnsaioCompressaoPDF({
  resultados = [],
  cabecalho = {},
  corAtiva = true,
  logoCliente = null,
  assinaturas = [],
}) {
  return (
    <Document>
      {/* Capa */}
      <Page size="A4" style={styles.coverPage}>
        <View style={styles.coverBrandRow}>
          <Image style={styles.coverIcon} src={logoIcon} />
          <View style={styles.coverBrandText}>
            <Text style={styles.coverWordmark}>concreto.tec.br</Text>
            <Text style={styles.coverTagline}>TECNOLOGIA EM CONCRETO</Text>
          </View>
        </View>

        <View style={styles.coverSpacer}>
          <View style={styles.coverTitleBlock}>
            <Text style={styles.coverKicker}>RELATÓRIO TÉCNICO DE ENSAIO</Text>
            <Text style={styles.coverTitle}>Ensaio de Compressão Axial de Corpos de Prova</Text>
            <Text style={styles.coverSubtitle}>NBR 5738:2015 e NBR 5739:2018</Text>

            <View style={styles.coverMetaGrid}>
              <View style={styles.coverMetaField}>
                <Text style={styles.coverMetaLabel}>CLIENTE</Text>
                <Text style={styles.coverMetaValue}>{cabecalho.cliente || '-'}</Text>
              </View>
              <View style={styles.coverMetaField}>
                <Text style={styles.coverMetaLabel}>OBRA</Text>
                <Text style={styles.coverMetaValue}>{cabecalho.obra || '-'}</Text>
              </View>
              <View style={styles.coverMetaField}>
                <Text style={styles.coverMetaLabel}>IDENTIFICAÇÃO DO RELATÓRIO</Text>
                <Text style={styles.coverMetaValue}>{cabecalho.identificacaoRelatorio || '-'}</Text>
              </View>
              <View style={styles.coverMetaField}>
                <Text style={styles.coverMetaLabel}>DATA DE EMISSÃO</Text>
                <Text style={styles.coverMetaValue}>{formatDateBrPdf(new Date())}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.coverFooter}>
          <Text>concreto.tec.br</Text>
          <Text>Documento gerado eletronicamente</Text>
        </View>
      </Page>

      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* Cabeçalho (fixed: repete em todas as páginas do PDF) */}
        <View style={styles.headerBox} fixed wrap={false}>
          <View style={styles.headerTop}>
            <View style={styles.headerTitleBlock}>
              <Text style={styles.headerTitle}>ENSAIO DE COMPRESSÃO AXIAL DE CORPOS DE PROVA</Text>
              <Text style={styles.headerSubtitle}>NBR 5738:2015 E 5739:2018</Text>
            </View>

            <View style={styles.headerNotice}>
              <Text style={styles.headerNoticeText}>{TEXTO_AVISO}</Text>
            </View>

            <View style={styles.headerBrandBlock}>
              <LogoRelatorio src={logoCliente} width={66} height={40} />
              <Text style={styles.headerBrandCaption}>Endereço</Text>
              <Text style={styles.headerAddressText}>{cabecalho.endereco || '-'}</Text>
            </View>
          </View>

          <View style={styles.headerLower}>
            <View style={styles.headerLowerLeft}>
              <View style={styles.headerIdBarRow}>
                <View style={styles.headerIdBar}>
                  <Text style={styles.headerIdBarLabel}>IDENTIFICAÇÃO DO RELATÓRIO</Text>
                  <Text style={styles.headerIdBarValue}>{cabecalho.identificacaoRelatorio || '-'}</Text>
                </View>
                <View style={styles.headerNoticeFill} />
              </View>

              <View style={styles.headerMetaRow}>
                <View style={[styles.metaCell, styles.metaCol]}>
                  <Text style={styles.metaLabel}>Empresa</Text>
                  <Text>{cabecalho.cliente || '-'}</Text>
                </View>
                <View style={[styles.metaCell, styles.metaCol]}>
                  <Text style={styles.metaLabel}>Cliente</Text>
                  <Text>{cabecalho.cliente || '-'}</Text>
                </View>
                <View style={[styles.metaCell, styles.metaCol]}>
                  <Text style={styles.metaLabel}>Obra</Text>
                  <Text>{cabecalho.obra || '-'}</Text>
                </View>
                <View style={[styles.metaCell, styles.metaCol]}>
                  <Text style={styles.metaLabel}>CNPJ</Text>
                  <Text>{cabecalho.cnpj || '-'}</Text>
                </View>
                <View style={[styles.metaCellLast, styles.metaCol]}>
                  <Text style={styles.metaLabel}>Nº CPs</Text>
                  <Text>{resultados.length}</Text>
                </View>
              </View>

              <View style={styles.headerMetaRow}>
                <View style={[styles.metaCell, styles.metaCol]}>
                  <Text style={styles.metaLabel}>Contato</Text>
                  <Text>{cabecalho.contato || '-'}</Text>
                </View>
                <View style={[styles.metaCell, styles.metaCol]}>
                  <Text style={styles.metaLabel}>E-mail</Text>
                  <Text>{cabecalho.email || '-'}</Text>
                </View>
                <View style={[styles.metaCell, styles.metaCol]}>
                  <Text style={styles.metaLabel}>Telefone</Text>
                  <Text>{cabecalho.telefone || '-'}</Text>
                </View>
                <View style={[styles.metaCell, styles.metaCol]}>
                  <Text style={styles.metaLabel}>Local</Text>
                  <Text>{cabecalho.local || '-'}</Text>
                </View>
                <View style={[styles.metaCellLast, styles.metaCol]}>
                  <Text style={styles.metaLabel}>Cimento</Text>
                  <Text>{cabecalho.cimento || '-'}</Text>
                </View>
              </View>

              <View style={styles.headerMetaRowLast}>
                <View style={[styles.metaCell, styles.metaCol]}>
                  <Text style={styles.metaLabel}>Fck</Text>
                  <Text>{cabecalho.fck || '-'}</Text>
                </View>
                <View style={[styles.metaCell, styles.metaCol]}>
                  <Text style={styles.metaLabel}>Tipo de Concreto</Text>
                  <Text>{cabecalho.tipoConcreto || '-'}</Text>
                </View>
                <View style={[styles.metaCell, styles.metaCol]}>
                  <Text style={styles.metaLabel}>Tipo de CP</Text>
                  <Text>{cabecalho.tipoCorpoProva || '-'}</Text>
                </View>
                <View style={[styles.metaCell, styles.metaCol]}>
                  <Text style={styles.metaLabel}>Concreteira</Text>
                  <Text>{cabecalho.concreteira || '-'}</Text>
                </View>
                <View style={[styles.metaCellLast, styles.metaCol]}>
                  <Text style={styles.metaLabel}>Tipo de Capeamento</Text>
                  <Text>{cabecalho.tipoCapeamento || '-'}</Text>
                </View>
              </View>
            </View>

            <View style={styles.headerQrColumn}>
              <View style={[styles.placeholderBox, styles.metaQrPlaceholder]}>
                <Text style={styles.placeholderText}>QR</Text>
              </View>
              <Text style={styles.metaQrCaption}>Armazenamento Digital do{'\n'}Cliente/Obra em Núvem</Text>
            </View>
          </View>
        </View>

        {/* Tabela */}
        <View style={styles.table}>
          {/* fixed: repete a linha de títulos no topo da tabela em toda página que ela ocupar */}
          <View style={[styles.tableRow, styles.tableHeaderRow]} fixed wrap={false}>
            <Text style={[styles.th, styles.colEtiqueta]}>Etiqueta</Text>
            <Text style={[styles.th, styles.colNotaFiscal]}>Nota{'\n'}Fiscal</Text>
            <Text style={[styles.th, styles.colVolume]}>Volume{'\n'}(m³)</Text>
            <Text style={[styles.th, styles.colPeca]}>Peça Concretada</Text>
            <Text style={[styles.th, styles.colSaidaUsina]}>Saída{'\n'}Usina</Text>
            <Text style={[styles.th, styles.colInicio]}>Início</Text>
            <Text style={[styles.th, styles.colFim]}>Fim</Text>
            <Text style={[styles.th, styles.colMoldagem]}>Data{'\n'}Moldagem</Text>
            <Text style={[styles.th, styles.colRuptura]}>Data{'\n'}Ruptura</Text>
            <Text style={[styles.th, styles.colIdade]}>Idade{'\n'}(dias)</Text>
            <Text style={[styles.th, styles.colSlump]}>Slump{'\n'}Aplic. (cm)</Text>
            <Text style={[styles.th, styles.colSlump]}>Slump{'\n'}NF (cm)</Text>
            <Text style={[styles.th, styles.colAgua]}>Adição{'\n'}Água (L)</Text>
            <Text style={[styles.th, styles.colTemp]}>Temp.{'\n'}Concreto (°C)</Text>
            <Text style={[styles.th, styles.colCarga]}>Ruptura{'\n'}(Ton)</Text>
            <Text style={[styles.th, styles.colFcj]}>Fcj{'\n'}(MPa)</Text>
            <Text style={[styles.th, styles.colTipoRuptura]}>Tipo de{'\n'}Ruptura</Text>
            <Text style={[styles.thLast, styles.colPrensa]}>Prensa</Text>
          </View>

          {resultados.map((item, index) => {
            const isEven = index % 2 === 1;
            const rowStyle = [styles.tableRow, isEven ? styles.tableRowEven : null];

            return (
              <View key={item.id || index} style={rowStyle} wrap={false}>
                <Text style={[styles.td, styles.colEtiqueta]}>{item.etiqueta || '-'}</Text>
                <Text style={[styles.td, styles.colNotaFiscal]}>{item.notaFiscal || '-'}</Text>
                <Text style={[styles.td, styles.colVolume]}>{item.volume ?? '-'}</Text>
                <Text style={[styles.td, styles.colPeca]}>{item.pecaConcretada || '-'}</Text>
                <Text style={[styles.td, styles.colSaidaUsina]}>{formatHoraPdf(item.dataSaidaUsina)}</Text>
                <Text style={[styles.td, styles.colInicio]}>{formatHoraPdf(item.dataInicioDescarga)}</Text>
                <Text style={[styles.td, styles.colFim]}>{formatHoraPdf(item.dataFimDescarga)}</Text>
                <Text style={[styles.td, styles.colMoldagem]}>{formatDateBrPdf(item.dataMoldagem)}</Text>
                <Text style={[styles.td, styles.colRuptura]}>{formatDateBrPdf(item.dataRuptura)}</Text>
                <Text style={[styles.td, styles.colIdade]}>{item.idadeRuptura ?? '-'}</Text>
                <Text style={[styles.td, styles.colSlump]}>{item.slumpAplicacao ?? '-'}</Text>
                <Text style={[styles.td, styles.colSlump]}>{item.slumpNotaFiscal ?? '-'}</Text>
                <Text style={[styles.td, styles.colAgua]}>{item.adicaoAgua ?? '-'}</Text>
                <Text style={[styles.td, styles.colTemp]}>{item.temperaturaConcreto ?? '-'}</Text>
                <Text style={[styles.td, styles.colCarga]}>{item.valrCarga ?? '-'}</Text>
                <Text
                  style={[
                    styles.td,
                    styles.colFcj,
                    styles.tdFcj,
                    // Fundo com a cor atual (alfa "73" ~45%); texto num tom mais escuro da mesma cor.
                    corAtiva && item.corFcj
                      ? { backgroundColor: `${CORES_FCJ[item.corFcj]}73`, color: CORES_FCJ_TEXTO[item.corFcj] }
                      : null,
                  ]}
                >
                  {formatNumeroPdf(item.fcj)}
                </Text>
                <Text style={[styles.td, styles.colTipoRuptura]}>{item.tipoRuptura || '-'}</Text>
                <Text style={[styles.tdLast, styles.colPrensa]}>{item.prensa || '-'}</Text>
              </View>
            );
          })}
        </View>

        {/* Rodapé (fixed: repete em todas as páginas de conteúdo, exceto a capa) */}
        <View style={styles.footer} fixed>
          <AssinaturasRodape assinaturas={assinaturas} />
          <View style={styles.footerPageRow}>
            <Text
              style={styles.footerText}
              render={({ pageNumber, totalPages }) => `${pageNumber}/${totalPages}`}
            />
          </View>
        </View>
      </Page>
    </Document>
  );
}

export default EnsaioCompressaoPDF;
